import { Box, Stack, styled } from '@mui/material';
import React, { useEffect, useState, type ComponentType } from 'react';
import type {
  ComposableMapProps,
  GeographiesProps,
  GeographyProps,
  MarkerProps,
} from 'react-simple-maps';
import {
  ComposableMap as RawComposableMap,
  Geographies as RawGeographies,
  Geography as RawGeography,
  Marker as RawMarker,
} from 'react-simple-maps';
import TagPill from './TagPill';

const ComposableMap = RawComposableMap as unknown as ComponentType<ComposableMapProps>;
const Geographies = RawGeographies as unknown as ComponentType<GeographiesProps>;
const Geography = RawGeography as unknown as ComponentType<GeographyProps>;
const Marker = RawMarker as unknown as ComponentType<MarkerProps>;


export interface MapAnnotation {
  coordinates: [number, number];
  onHoverTooltip?: React.ReactElement;
  onClick?: () => void;
  id: string;
}

export interface ZoomRegion {
  id: string;
  name: string;
  center: [number, number];
  scale: number;
}

export interface InteractiveMapProps {
  annotations?: MapAnnotation[];
  highlightedCountries?: string[];
  selectedCountry?: string;
  onCountryClick?: (countryCode: string) => void;
  projection?: 'geoNaturalEarth1' | 'geoMercator' | 'geoEqualEarth';
  className?: string;
}

const MapContainer = styled(Box)({
  width: '100%',
  height: 'fit-content',
});

const ButtonsContainer = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  flexDirection: 'row',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

const TooltipContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  pointerEvents: 'none',
  zIndex: 9999999,
  marginTop: '-8px',
}));

// The scale depends on the width of the component, need to calculate it

// Predefined regions for button navigation
const regions: ZoomRegion[] = [
  {
    id: 'world',
    name: 'World',
    center: [0, 0],
    scale: 300,
  },
  {
    id: 'north-america',
    name: 'North America',
    center: [-95, 42],
    scale: 1200,
  },
  {
    id: 'caribbean',
    name: 'Caribbean',
    center: [-75, 18],
    scale: 1600,
  },
  {
    id: 'europe',
    name: 'Europe',
    center: [5, 45],
    scale: 2500,
  },
  {
    id: 'asia',
    name: 'Asia',
    center: [120, 25],
    scale: 1200,
  },
  {
    id: 'middle-east',
    name: 'Middle East',
    center: [55.3, 25.3],
    scale: 2500,
  },
  {
    id: 'oceania',
    name: 'Oceania',
    center: [151.21, -33.87],
    scale: 1000,
  },
];

// Helper function to determine country fill color
const getCountryFill = (countryCode: string, highlightedCountries: string[], selectedCountry?: string) => {
  if (selectedCountry === countryCode) return "#16A34A"; // Dark green for selected
  if (highlightedCountries.includes(countryCode)) return "#86EFAC"; // Light green for highlighted
  return "#E5E7EB"; // Default gray
};

export const InteractiveMap: React.FC<InteractiveMapProps> = ({
  annotations = [],
  highlightedCountries = [],
  selectedCountry,
  onCountryClick,
  projection = 'geoNaturalEarth1',
  className,
}) => {
  const [width, setWidth] = useState<number>(800);
  const [mapCenter, setMapCenter] = useState<[number, number]>([0, 0]);
  const [mapScale, setMapScale] = useState<number>(300);
  const [selectedRegion, setSelectedRegion] = useState<string>('world');


  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);


  const [hoveredAnnotation, setHoveredAnnotation] = useState<{
    annotation: MapAnnotation;
    position: { x: number; y: number };
    isRightEdge: boolean;
  } | null>(null);

  const handleMarkerMouseEnter = (
    annotation: MapAnnotation,
    event: React.MouseEvent
  ) => {
    if (annotation.onHoverTooltip) {
      const rect = event.currentTarget.getBoundingClientRect();
      const tooltipWidth = 280; // CityCard width
      const windowWidth = window.innerWidth;
      const markerCenterX = rect.left + rect.width / 2;

      // Check if tooltip would overflow right edge
      const wouldOverflowRight = markerCenterX + tooltipWidth / 2 > windowWidth - 20; // 20px margin

      setHoveredAnnotation({
        annotation,
        position: {
          x: markerCenterX,
          y: rect.top,
        },
        isRightEdge: wouldOverflowRight,
      });
    }
  };

  const handleMarkerMouseLeave = () => {
    setHoveredAnnotation(null);
  };

  const handleRegionClick = (regionId: string) => {
    const region = regions.find(r => r.id === regionId);
    if (region) {
      setMapCenter(region.center);
      setMapScale(region.scale);
      setSelectedRegion(regionId);
    }
  };


  return (
    <MapContainer className={className}>
      <ButtonsContainer>
        {regions.map((region, index) => (
          <TagPill
            text={region.name}
            key={region.id}
            emphasize={selectedRegion === region.id}
            autocolor={index}
            onClick={() => handleRegionClick(region.id)}
          />
        ))}
      </ButtonsContainer>
      <ComposableMap
        projection={projection}
        projectionConfig={{
          scale: mapScale,
          center: mapCenter,
        }}
        width={width}
        height={width * 0.75}
      >
        <Geographies geography={'/map.json'}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const countryCode = geo.properties.ISO_A2_EH;
              const isHighlighted = highlightedCountries.includes(countryCode);
              const isSelected = selectedCountry === countryCode;

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onClick={() => {
                    if (onCountryClick) {
                      onCountryClick(countryCode);
                    }
                  }}
                  stroke="#FFFFFF"
                  strokeWidth={0.5}
                  style={{
                    default: {
                      fill: getCountryFill(countryCode, highlightedCountries, selectedCountry),
                      outline: "none",
                      cursor: isHighlighted ? "pointer" : "default",
                    },
                    hover: {
                      fill: isSelected ? "#15803D" : (isHighlighted ? "#22C55E" : "#D1D5DB"),
                      outline: "none",
                      cursor: isHighlighted ? "pointer" : "default",
                    },
                    pressed: {
                      fill: isSelected ? "#14532D" : (isHighlighted ? "#16A34A" : "#9CA3AF"),
                      outline: "none",
                    },
                  }}
                />
              );
            })
          }
        </Geographies>

        {annotations.map((annotation) => (
          <Marker
            key={annotation.id}
            coordinates={annotation.coordinates}
            onMouseEnter={(event) => handleMarkerMouseEnter(annotation, event)}
            onMouseLeave={handleMarkerMouseLeave}
            onClick={annotation.onClick}
          >
            <circle
              r={6}
              fill="#EF4444"
              stroke="#FFFFFF"
              strokeWidth={2}
            />
          </Marker>
        ))}
      </ComposableMap>

      {hoveredAnnotation && (
        <TooltipContainer
          style={{
            left: hoveredAnnotation.position.x,
            top: hoveredAnnotation.position.y,
            transform: hoveredAnnotation.isRightEdge
              ? 'translate(-100%, -100%)'
              : 'translate(-50%, -100%)',
          }}
        >
          {hoveredAnnotation.annotation.onHoverTooltip}
        </TooltipContainer>
      )}
    </MapContainer>
  );
};

export default InteractiveMap;
